import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { HelpCircle, X } from "lucide-react";
import { findTour, DEFAULT_SPOTS } from "../lib/tours";

const visible = (el) => { const r = el.getBoundingClientRect(); return r.width > 20 && r.height > 0 && r.right > 0 && r.left < window.innerWidth - 20 && getComputedStyle(el).visibility !== "hidden"; };
const pick = (sel) => { try { return [...document.querySelectorAll(sel)].find(visible) || null; } catch { return null; } };

function Spotlight({ step, i, n, onNext, onBack, onClose }) {
  const [rect, setRect] = useState(null);
  const [sel, title, text] = step;
  useLayoutEffect(() => {
    const el = pick(sel || DEFAULT_SPOTS[i] || "h1");
    if (el) el.scrollIntoView({ block: el.getBoundingClientRect().height > window.innerHeight * 0.6 ? "start" : "center", behavior: "smooth" });
    const measure = () => setRect(el ? el.getBoundingClientRect() : null);
    const t = setTimeout(measure, el ? 400 : 0);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("scroll", measure, true); window.removeEventListener("resize", measure); };
  }, [sel, i]);
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const pad = 8;
  const below = rect && rect.bottom + 220 < window.innerHeight;
  const side = rect && rect.height > window.innerHeight * 0.6 && rect.right + 350 < window.innerWidth;
  const tall = rect && rect.height > window.innerHeight * 0.6;
  const card = side ? { top: 96, left: rect.right + 24 } : tall ? { bottom: 16, left: "50%", transform: "translateX(-50%)" } : rect
    ? { top: below ? rect.bottom + pad + 12 : Math.max(12, rect.top - pad - 12 - 200), left: Math.min(Math.max(12, rect.left), window.innerWidth - 332) }
    : { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  return (
    <div className="fixed inset-0 z-[100]" data-testid="tour-overlay">
      {rect ? (
        <div className="absolute rounded-2xl ring-2 ring-[var(--gold)] transition-[top,left,width,height] duration-300 pointer-events-none"
          style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, boxShadow: "0 0 0 9999px rgba(10,10,10,0.62)" }} data-testid="tour-spotlight" />
      ) : <div className="absolute inset-0 bg-black/60" />}
      <div className="absolute w-80 bg-white text-[var(--ink)] rounded-2xl p-5 shadow-2xl" style={card} data-testid="tour-card">
        <button onClick={onClose} className="absolute top-3 right-3 text-[var(--muted)]" data-testid="tour-close" aria-label="Close guide"><X className="w-4 h-4" /></button>
        <div className="flex gap-1.5 mb-3">{Array.from({ length: n }, (_, k) => <span key={k} className={`h-1.5 rounded-full transition-[width] ${k === i ? "w-8 bg-[var(--gold)]" : "w-3 bg-[var(--line)]"}`} />)}</div>
        <p className="font-serif text-xl" data-testid="tour-title">{title}</p>
        <p className="text-sm text-[var(--muted)] mt-1.5">{text}</p>
        <div className="flex items-center gap-2 mt-4">
          <button onClick={onClose} className="text-xs text-[var(--muted)] underline" data-testid="tour-skip">Skip</button>
          {i > 0 && <button onClick={onBack} className="ml-auto text-xs px-3 py-2 rounded-full border border-[var(--line)]" data-testid="tour-back">Back</button>}
          <button onClick={onNext} className={`${i > 0 ? "" : "ml-auto"} btn-gold !py-2 !px-5 text-xs`} data-testid="tour-next">{i === n - 1 ? "Got it" : "Next"}</button>
        </div>
      </div>
    </div>
  );
}

const NO_HELP = /^\/(party-screen|news-wall|print|waiver|agreements\/sign|tv)/;
const NO_AUTO = /^\/site\/rohan-and-anjali-patel/;

export function TourGuide() {
  const { pathname } = useLocation();
  const tour = NO_HELP.test(pathname) ? null : findTour(pathname);
  const [i, setI] = useState(-1);
  const key = tour && `lif_tour_${tour.id}`;
  useEffect(() => {
    setI(-1);
    if (!tour || localStorage.getItem(key) || NO_AUTO.test(pathname) || window.self !== window.top) return undefined;
    const t = setTimeout(() => setI(0), 1200);
    return () => clearTimeout(t);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = useCallback(() => { setI(-1); if (key) localStorage.setItem(key, "1"); }, [key]);
  if (!tour) return null;
  return (
    <>
      <button onClick={() => setI(0)} className={`fixed right-4 z-40 w-11 h-11 rounded-full bg-[var(--ink)] text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform ${/\/social/.test(pathname) ? "top-16" : "bottom-24 lg:bottom-6"}`} data-testid="tour-help-btn" aria-label="How this page works">
        <HelpCircle className="w-5 h-5" />
      </button>
      {i >= 0 && <Spotlight step={tour.steps[i]} i={i} n={tour.steps.length} onBack={() => setI(i - 1)} onClose={close} onNext={() => (i === tour.steps.length - 1 ? close() : setI(i + 1))} />}
    </>
  );
}
